import type { Project } from './types';

export const PROJECTS: Project[] = [
  {
    id: 'hmx',
    title: 'HMX - HashMicro AI Native System',
    description: 'AI-native ERP system with intent-based workflows, predictive alerts, and Hashy Assistant.',
    longDescription: 'HMX is an AI-native system integrated directly into HashMicro ERP software. It replaces rigid workflows with intent-based interactions, helping business users act through AI-native automation, predictive alerts, business recommendations, AI Insight Briefs, and Hashy Assistant inside operational ERP workflows.',
    image: '/projects/hmx-partner-ai-dashboard.png',
    tags: ['AI Native', 'ERP', 'Frontend', 'AI Assistant', 'HashMicro'],
    role: 'Frontend Developer',
    organization: 'HashMicro',
    period: 'December 2025 - Present',
    highlights: [
      'Built ERP-facing AI interfaces for partner operations and purchase dashboards.',
      'Integrated AI Insight Brief actions into business workflow surfaces.',
      'Supported Hashy Assistant experiences for contextual recommendations and follow-up actions.',
    ],
    evidenceImages: [
      '/projects/hmx-partner-ai-dashboard.png',
      '/projects/hmx-hashy-assistant.png',
      '/projects/hmx-partner-teams.png',
    ],
    demo: 'https://www.hashmicro.com/',
    featured: true,
    year: '2025',
    status: 'in-progress',
  },
  {
    id: 'agrifertile',
    title: 'Agrifertile',
    description: 'AI-powered agricultural fertilizer recommendation system with RAG-based product guidance.',
    longDescription: 'Agrifertile is an AI-powered agricultural recommendation platform for PT Petrokimia Gresik. It uses Retrieval-Augmented Generation to provide fertilizer guidance, product promotion support, document-grounded answers, and agricultural insights for field and sales contexts.',
    image: '/projects/agrifertile-overview.jpg',
    tags: ['Laravel', 'Flutter', 'RAG', 'OpenAI'],
    role: 'Full Stack Developer / AI Engineer',
    organization: 'PT Petrokimia Gresik',
    period: 'August 2025 - Present',
    highlights: [
      'Delivered RAG-based fertilizer recommendations grounded in product knowledge.',
      'Built mobile and web-facing flows for agricultural consultation.',
      'Connected AI assistance to product promotion and agronomic decision support.',
    ],
    evidenceImages: [
      '/projects/agrifertile-overview.jpg',
      '/projects/agrifertile-nova-ai.jpg',
    ],
    featured: true,
    year: '2025',
    status: 'in-progress',
  },
  {
    id: 'p-men-rag',
    title: 'P-MEN RAG System',
    description: 'AI-powered document processing and knowledge management system with OCR capabilities.',
    longDescription: 'P-MEN is an AI-powered Retrieval-Augmented Generation system for document processing, OCR, multi-format document support, knowledge management, and context-aware responses using OpenAI and Google Gemini.',
    image: '/projects/p-men-rag-dashboard.png',
    tags: ['Python', 'FastAPI', 'LangChain', 'OCR', 'OpenAI', 'Gemini'],
    role: 'AI / Backend Developer',
    organization: 'PT Petrokimia Gresik',
    period: 'June 2025 - September 2025',
    highlights: [
      'Implemented document ingestion and OCR processing for internal knowledge workflows.',
      'Built RAG endpoints for contextual answers across uploaded documents.',
      'Integrated OpenAI and Gemini-based reasoning into a knowledge management flow.',
    ],
    evidenceImages: [
      '/projects/p-men-rag-dashboard.png',
      '/projects/p-men-rag-landing-login.png',
      '/projects/p-men-rag-crud.png',
      '/projects/p-men-rag-user-page.png',
    ],
    featured: true,
    year: '2025',
    status: 'completed',
  },
  {
    id: 'flownova',
    title: 'FlowNova',
    description: 'AI RAG chatbot workflow for product recommendations and company knowledge access.',
    longDescription: 'FlowNova is an AI-powered chatbot workflow for intelligent product recommendations and company knowledge management. It uses workflow automation, RAG, vector search, and real-time context-aware responses to improve customer support efficiency.',
    image: '/projects/flownova-workflow.jpg',
    tags: ['n8n', 'OpenAI', 'LangChain', 'Vector DB'],
    role: 'AI Workflow Developer',
    organization: 'PT Petrokimia Gresik',
    period: 'April 2025 - May 2025',
    highlights: [
      'Designed n8n workflow automation for RAG chatbot orchestration.',
      'Connected company knowledge sources to vector-based retrieval.',
      'Supported product recommendation flows for customer and sales scenarios.',
    ],
    evidenceImages: ['/projects/flownova-workflow.jpg'],
    featured: true,
    year: '2025',
    status: 'completed',
  },
  {
    id: 'apm',
    title: 'APM - Assets Performance Management',
    description: 'Enterprise asset performance management for monitoring, analytics, and maintenance workflows.',
    longDescription: 'APM is an enterprise asset performance management system for PT Petrokimia Gresik. It supports asset monitoring, KPI analytics, automated maintenance scheduling, reporting, and data-driven operational decisions.',
    image: '/projects/apm-dashboard.png',
    tags: ['Laravel', 'Vue.js', 'Google Cloud'],
    role: 'Full Stack Developer',
    organization: 'PT Petrokimia Gresik',
    period: 'December 2024 - April 2025',
    highlights: [
      'Built dashboard and KPI monitoring flows for asset performance tracking.',
      'Implemented data views for reliability, availability, target, and report analysis.',
      'Supported cloud deployment and production maintenance workflows.',
    ],
    evidenceImages: [
      '/projects/apm-dashboard.png',
      '/projects/apm-overall-report.png',
    ],
    featured: true,
    year: '2024',
    status: 'completed',
  },
  {
    id: 'petromart',
    title: 'Petromart E-Commerce Platform',
    description: 'Internal e-commerce platform with marketplace omnichannel and inventory synchronization.',
    longDescription: 'Petromart is an internal e-commerce platform for PT Petrokimia Gresik with product catalog management, payment integration, Ginee marketplace omnichannel support, multi-channel inventory sync, CI/CD, and order management.',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&auto=format&fit=crop',
    tags: ['Laravel', 'Python', 'Google Cloud', 'CI/CD'],
    role: 'Full Stack Developer',
    organization: 'PT Petrokimia Gresik',
    period: 'June 2024 - January 2025',
    highlights: [
      'Implemented product catalog, order, and inventory management flows.',
      'Integrated marketplace omnichannel operations through Ginee synchronization.',
      'Supported payment and deployment workflows for internal commerce operations.',
    ],
    year: '2025',
    status: 'completed',
  },
  {
    id: 'pmb-polije',
    title: 'PMB Politeknik Negeri Jember',
    description: 'Public online admission system for Politeknik Negeri Jember, built as a full-stack project.',
    longDescription: 'PMB Polije is a full-stack student admission system for Politeknik Negeri Jember. It covers registration pathways, announcements, important files, graduation checks, document verification, and online registration flows.',
    image: '/projects/pmb-polije-home.png',
    tags: ['Laravel', 'Vue.js', 'Full Stack', 'Admission'],
    role: 'Full Stack Developer',
    organization: 'Politeknik Negeri Jember',
    period: '2024',
    highlights: [
      'Developed public-facing admission pages and registration pathway flows.',
      'Built supporting features for announcements, file access, and graduation checks.',
      'Maintained full-stack admission functionality for prospective students.',
    ],
    evidenceImages: [
      '/projects/pmb-polije-home.png',
      '/projects/pmb-polije-pathways.png',
    ],
    demo: 'https://pmb.polije.ac.id/',
    year: '2024',
    status: 'completed',
  },
  {
    id: 'pmb-stikes-bhakti-alqodiri',
    title: 'PMB Online - STIKES Bhakti Al-Qodiri',
    description: 'Online student admission app for STIKES Bhakti Al-Qodiri, built as a full-stack project.',
    longDescription: 'PMB Online is a full-stack online student admission application for STIKES Bhakti Al-Qodiri. The system helps manage hundreds of registrants, improve accountability, analyze applicant demographics, and support online payment workflows with BNI VA.',
    image: '/projects/pmb-stikes-bhakti-alqodiri.png',
    tags: ['Laravel', 'Vue.js', 'MySQL', 'BNI VA', 'Full Stack'],
    role: 'Full Stack Developer',
    organization: 'Dafidea / STIKES Bhakti Al-Qodiri',
    period: '2023',
    highlights: [
      'Built admission registration and administration features for a campus PMB flow.',
      'Supported applicant demographic reporting and operational accountability.',
      'Integrated payment workflow support using BNI VA.',
    ],
    evidenceImages: ['/projects/pmb-stikes-bhakti-alqodiri.png'],
    demo: 'https://dafidea.com/en/works/pmb-online-stikes-bhakti-alqodiri-online-student-admission-app/',
    year: '2023',
    status: 'completed',
  },
  {
    id: 'futura-operation',
    title: 'Futura Operation App',
    description: 'Backend development for PT Futura Energi Indonesia operational project management app.',
    longDescription: 'Futura Operation App is a project management and operational tracking app owned by PT Futura Energi Indonesia. The backend work focused on APIs and server-side support for project management data, team roles, checklist items, operational progress, and admin panel enhancements.',
    image: '/projects/futura-operation-app.png',
    tags: ['Laravel', 'REST API', 'MySQL', 'Backend'],
    role: 'Backend Developer',
    organization: 'Dafidea / PT Futura Energi Indonesia',
    period: '2023',
    highlights: [
      'Developed backend APIs for mobile operational project management flows.',
      'Supported server-side data for roles, checklists, project progress, and item tracking.',
      'Helped connect admin and mobile experiences through structured REST endpoints.',
    ],
    evidenceImages: ['/projects/futura-operation-app.png'],
    demo: 'https://dafidea.com/en/works/futura-pt-futura-energi-indonesia-fei-operational-app/',
    year: '2023',
    status: 'completed',
  },
  {
    id: 'epromosi-petrokimia',
    title: 'E-Promosi Petrokimia',
    description: 'Digital marketing and reporting platform for sales, promotion, and agronomic data.',
    longDescription: 'E-Promosi Petrokimia is a digital marketing and reporting platform used across the Pupuk Indonesia business group. The system supports PT Petrokimia Gresik and PT Petrokimia Kayaku through dedicated portals for sales activities, promotion reporting, and agronomic data management.',
    image: '/projects/riset-e-promosi-dashboard.png',
    tags: ['Laravel', 'jQuery', 'CI/CD', 'Reporting'],
    role: 'Full Stack Developer',
    organization: 'PT Petrokimia Gresik / PT Petrokimia Kayaku',
    period: '2024',
    highlights: [
      'Built digital promotion and reporting features for sales and agronomic activities.',
      'Supported separate portal needs for Petrokimia Gresik and Petrokimia Kayaku.',
      'Improved reporting visibility for marketing, promotion, and field data.',
    ],
    evidenceImages: [
      '/projects/riset-e-promosi-dashboard.png',
      '/projects/riset-siasep-e-promosi-landing.png',
    ],
    year: '2024',
    status: 'completed',
  },
  {
    id: 'siasep',
    title: 'SIASEP',
    description: 'Digital platform context for subsidized fertilizer workflows connected to i-Pubers.',
    longDescription: 'SIASEP is connected to subsidized fertilizer digitalization in the Petrokimia and Pupuk Indonesia ecosystem. It supports workflows related to i-Pubers, a digital platform used to help manage subsidized fertilizer processes, operational monitoring, and reporting visibility.',
    image: '/projects/riset-siasep-dashboard.png',
    tags: ['Laravel', 'jQuery', 'CI/CD', 'Dashboard'],
    role: 'Full Stack Developer',
    organization: 'PT Petrokimia Gresik / Pupuk Indonesia',
    period: '2024',
    highlights: [
      'Supported subsidized fertilizer digitalization workflows in the Petrokimia ecosystem.',
      'Built dashboard and operational views connected to i-Pubers-related processes.',
      'Helped improve traceability for fertilizer distribution and reporting workflows.',
    ],
    evidenceImages: [
      '/projects/riset-siasep-dashboard.png',
      '/projects/riset-siasep-e-promosi-landing.png',
    ],
    year: '2024',
    status: 'completed',
  },
  {
    id: 'dpr-sniper',
    title: 'DPR SNIPER - Electronic Correspondence System',
    description: 'DPR RI electronic correspondence platform for incoming mail, outgoing letters, digital signatures, warrant disposition, reporting, and archive management.',
    longDescription: 'DPR SNIPER, short for Sistem Informasi Persuratan Elektronik, is an electronic official correspondence and document administration system used within the Secretariat General of the House of Representatives of the Republic of Indonesia (DPR RI). The platform supports a modern parliament vision by digitizing official letter workflows, integrating digital signatures, and making DPR RI correspondence governance more efficient, traceable, and archive-ready.',
    image: '/projects/dpr-sniper.png',
    tags: ['Laravel Lumen', 'Vue.js', 'E-Sign API', 'Government', 'DPR RI'],
    role: 'Backend Developer',
    organization: 'Dafidea / DOT Indonesia / Secretariat General of DPR RI',
    period: 'November 2022 - February 2023',
    highlights: [
      'Built backend services for DPR RI electronic correspondence workflows.',
      'Supported incoming letter registration, outgoing letter creation, signing, warrant disposition, warrant reporting, and mail archiving.',
      'Integrated electronic signature capabilities to support DPR RI digital document governance.',
      'Contributed to a government-grade system aligned with SPBE and modern parliament digital transformation.',
    ],
    evidenceImages: ['/projects/dpr-sniper.png'],
    demo: 'https://www.tribunnews.com/dpr-ri/2021/08/30/setjen-dpr-resmikan-penggunaan-ttd-digital-dan-aplikasi-tata-naskah-dinas-elektronik',
    year: '2023',
    status: 'completed',
  },
  {
    id: 'hcms-sier',
    title: 'HCMS PT SIER',
    description: 'Human capital management system for employee data, attendance, and performance evaluation.',
    longDescription: 'HCMS PT SIER is a Human Capital Management System developed with Dafidea Technocraft. It manages employee data, attendance, performance evaluations, leave management, payroll integration, and HR dashboards.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop',
    tags: ['Laravel', 'Vue.js'],
    role: 'Full Stack Developer',
    organization: 'Dafidea / PT SIER',
    period: 'December 2023 - April 2024',
    highlights: [
      'Developed HR management features for employee and attendance records.',
      'Supported performance, leave, payroll, and dashboard modules.',
      'Helped modernize internal HCMS operations for PT SIER.',
    ],
    year: '2024',
    status: 'completed',
  },
];

export const ALL_TAGS = Array.from(new Set(PROJECTS.flatMap(p => p.tags))).sort();
